import { useMemo } from 'react';
import { generatePricingModel, type IdeaData, type PricingModel } from '@/lib/pricing';

/**
 * Hook that generates pricing model and handles legacy data migration
 * @param idea - The startup idea data
 * @returns PricingModel - Generated pricing model
 */
export const usePricing = (idea: IdeaData | null | undefined): PricingModel => {
  return useMemo(() => {
    if (!idea) {
      // Return minimal default pricing if no idea
      return {
        summary: 'Pricing model not available',
        tiers: [
          { name: 'Free', price: '$0', shortTagline: 'Basic access', longDescription: 'Free tier with limited features' },
          { name: 'Pro', price: '$29/mo', shortTagline: 'Full access', longDescription: 'Pro tier with all features' },
          { name: 'Enterprise', price: 'Custom', shortTagline: 'Custom solution', longDescription: 'Enterprise tier with custom pricing' },
        ],
        model: 'Freemium',
      };
    }

    // Generate pricing using the single source of truth
    const pricing = generatePricingModel(idea);

    // Migration logic: If idea has legacy 'offer' field, we now ignore it
    // The generatePricingModel function is the single source of truth
    // Legacy offer data is preserved in DB but not used for display

    return pricing;
  }, [idea]);
};

/**
 * Hook specifically for migrating legacy ideas on read
 * This returns both the pricing model and a flag indicating if migration is needed
 */
export const usePricingWithMigration = (idea: IdeaData | null | undefined) => {
  const pricing = usePricing(idea);
  
  const needsMigration = useMemo(() => {
    if (!idea) return false;
    
    // Check if idea has legacy offer field but no modern pricing data
    const hasLegacyOffer = idea.offer && typeof idea.offer === 'object' && Object.keys(idea.offer).length > 0;
    const hasModernPricing = Boolean(idea.pricing_model);
    
    return hasLegacyOffer && !hasModernPricing;
  }, [idea]);

  return {
    pricing,
    needsMigration,
  };
};